import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import Input from "../common/Input";
import Button from "../common/Button";
import Modal from "../common/Modal";
import { toDisplayDateInput } from "../../utils/dateUtils";

interface ActiveScreenReportFilterFormData {
  date?: string;
}

interface ActiveScreenReportFilterModalProps {
  visible: boolean;
  initialData?: Partial<ActiveScreenReportFilterFormData>;
  onSubmit: (data: ActiveScreenReportFilterFormData) => void;
  onCancel: () => void;
  onClose: () => void;
  isLoading?: boolean;
  // Optional callback to clear applied filters in parent while keeping the modal open
  onReset?: () => void;
}

const ActiveScreenReportFilterModal: React.FC<ActiveScreenReportFilterModalProps> = ({
  visible,
  initialData,
  onSubmit,
  onCancel,
  onClose,
  isLoading = false,
  onReset,
}) => {
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    trigger,
  } = useForm<ActiveScreenReportFilterFormData>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: initialData,
  });

  const dateValue = watch("date");

  // Get today's date in YYYY-MM-DD format for max attribute
  const today = toDisplayDateInput();

  useEffect(() => {
    reset(initialData);
  }, [initialData, reset]);

  const handleReset = () => {
    // Clear the form field
    reset({});
    // Let parent clear its selected date but keep modal open
    onReset?.();
  };

  return (
    <Modal isOpen={visible} onClose={onClose} title="Filter Active Screens Report">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Date"
          type="date"
          name="date"
          value={dateValue || ""}
          onChange={(e) => setValue("date", e.target.value || undefined, { shouldValidate: false })}
          onBlur={() => trigger("date")}
          error={errors.date}
          max={today}
          placeholder="Select Date (Optional)"
        />
        <p className="text-sm text-gray-500">
          Select a date to view active screens for that particular date. Only previous dates are allowed.
        </p>
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleReset}
            disabled={isLoading}
          >
            Reset
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isLoading}
            className="w-full sm:w-auto"
          >
            Apply
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ActiveScreenReportFilterModal;
